<template>
  <div>
    <!-- Summary Cards -->
    <div class="row" v-if="summary && isGraph">
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-aqua">
          <div class="inner">
            <h3>{{ summary.totalBranches }}</h3>
            <p>Total Branches</p>
          </div>
          <div class="icon">
            <i class="fa fa-building"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-green">
          <div class="inner">
            <h3>{{ formatCurrency(summary.totalInvoices) }}</h3>
            <p>Total Revenue</p>
          </div>
          <div class="icon">
            <i class="fa fa-money"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-yellow">
          <div class="inner">
            <h3>{{ formatCurrency(summary.totalProfit) }}</h3>
            <p>Total Profit</p>
          </div>
          <div class="icon">
            <i class="fa fa-line-chart"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-red">
          <div class="inner">
            <h3>{{ summary.totalJobs }}</h3>
            <p>Total Jobs</p>
          </div>
          <div class="icon">
            <i class="fa fa-file-text"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="row">
      <!-- Top Branches by Revenue -->
      <div class="col-md-6" v-if="isGraph">
        <div class="box box-primary">
          <div class="box-header with-border">
            <h3 class="box-title">Top 10 Branches by Revenue</h3>
          </div>
          <div class="box-body">
            <apexchart
              type="bar"
              height="350"
              :options="topBranchesChartData.options"
              :series="topBranchesChartData.series"
              v-if="records.length > 0"
            ></apexchart>
            <div v-else style="height: 350px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="text-align: center; color: #666;">
                <i class="fa fa-bar-chart fa-2x"></i>
                <p style="margin-top: 10px;">No data available</p>
                <small>Please load data to view charts</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Branch Performance Distribution -->
      <div class="col-md-6" v-if="isGraph">
        <div class="box box-warning">
          <div class="box-header with-border">
            <h3 class="box-title">Branch Performance Distribution</h3>
          </div>
          <div class="box-body">
            <apexchart
              type="pie"
              height="350"
              :options="branchPerformanceChartData.options"
              :series="branchPerformanceChartData.series"
              v-if="records.length > 0"
            ></apexchart>
            <div v-else style="height: 350px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="text-align: center; color: #666;">
                <i class="fa fa-pie-chart fa-2x"></i>
                <p style="margin-top: 10px;">No data available</p>
                <small>Please load data to view charts</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="box box-default">
      <div class="box-header with-border">
        <h3 class="box-title">Branch-wise Growth Data</h3>
        <div class="box-tools pull-right">
          <button type="button" class="btn btn-success btn-sm" @click="exportToExcel">
            <i class="fa fa-download"></i> Export Excel
          </button>
        </div>
      </div>
      <div class="box-body">
        <b-table
          :items="paginatedRecords"
          :fields="tableFields"
          :tbody-tr-class="getRowClass"
          striped
          bordered
          hover
          responsive="sm"
          class="text-center"
          :per-page="perPage"
          :current-page="currentPage"
        >
          <template slot="sn" slot-scope="data">
            {{ data.index + 1 }}
          </template>

          <template slot="branchId" slot-scope="data">
            <span class="text-center d-block">{{ data.value }}</span>
          </template>

          <template slot="branchLocation" slot-scope="data">
            <span class="text-left d-block">{{ data.value }}</span>
          </template>

          <template slot="branchMainLocation" slot-scope="data">
            <span class="text-center d-block">{{ data.value }}</span>
          </template>

          <template slot="totalJobs" slot-scope="data">
            <span class="text-center d-block">{{ data.value }}</span>
          </template>

          <template slot="totalInvoices" slot-scope="data">
            <span class="text-right d-block">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="totalExpenses" slot-scope="data">
            <span class="text-right d-block">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="totalProfit" slot-scope="data">
            <span class="text-right d-block" :class="getProfitClass(data.value)">
              {{ formatCurrency(data.value) }}
            </span>
          </template>

          <template slot="avgInvoice" slot-scope="data">
            <span class="text-right d-block">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="avgProfit" slot-scope="data">
            <span class="text-right d-block" :class="getProfitClass(data.value)">
              {{ formatCurrency(data.value) }}
            </span>
          </template>

          <template slot="profitPercentage" slot-scope="data">
            <span class="text-right d-block" :class="getPercentageClass(data.value)">
              {{ data.value.toFixed(2) }}%
            </span>
          </template>

          <template slot="status" slot-scope="data">
            <span class="label" :class="getStatusClass(data.value)">
              {{ data.value }}
            </span>
          </template>

          <template slot="bottom-row" v-if="summary">
            <td colspan="4" class="text-center font-weight-bold bg-light">TOTAL</td>
            <td class="text-center font-weight-bold bg-light">{{ summary.totalJobs }}</td>
            <td class="text-right font-weight-bold bg-light">{{ formatCurrency(summary.totalInvoices) }}</td>
            <td class="text-right font-weight-bold bg-light">{{ formatCurrency(summary.totalExpenses) }}</td>
            <td class="text-right font-weight-bold bg-light" :class="getProfitClass(summary.totalProfit)">
              {{ formatCurrency(summary.totalProfit) }}
            </td>
            <td colspan="4" class="bg-light"></td>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalRows > perPage">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0">Show:</label>
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">entries</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            class="mb-0"
            first-number
            last-number
          ></b-pagination>

          <div class="text-muted small">
            Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage * perPage, totalRows) }} of {{ totalRows }} entries
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GrowthDashboardService from '../../../services/GrowthDashboardService';
import VueApexCharts from 'vue-apexcharts';
import * as XLSX from 'xlsx';
// Chart functionality temporarily disabled
// import Chart from 'chart.js';

export default {
  name: 'BranchWiseGrowthComponent',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    filters: {
      type: Object,
      required: true
    },
    isGraph: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      records: [],
      summary: null,
      // Pagination
      currentPage: 1,
      perPage: 50,
      pageOptions: [25, 50, 100, 200],
      totalRows: 0,
      sortBy: null,
      sortDesc: false,
      sortDirection: 'asc',
      filter: null
    };
  },

  watch: {
    records(newVal) {
      this.totalRows = newVal.length;
    }
  },

  computed: {
    paginatedRecords() {
      return this.records.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },
    // Chart data for Top Branches Vertical Bar Chart
    topBranchesChartData() {
      if (!this.records || this.records.length === 0) {
        return {
          series: [],
          options: {}
        };
      }

      // Sort branches by total profit and take top 10
      const topBranches = [...this.records]
        .sort((a, b) => (b.totalProfit || 0) - (a.totalProfit || 0))
        .slice(0, 10);

      return {
        series: [{
          name: 'Total Profit',
          data: topBranches.map(branch => parseFloat(branch.totalProfit) || 0)
        }],
        options: {
          chart: {
            type: 'bar',
            height: 350,
            toolbar: {
              show: true
            }
          },
          plotOptions: {
            bar: {
              columnWidth: '60%',
              borderRadius: 4
            }
          },
          dataLabels: {
            enabled: true,
            formatter: function(value) {
              return '₹' + (value / 100000).toFixed(0) + 'L';
            }
          },
          xaxis: {
            categories: topBranches.map(branch => branch.branchName || 'Unknown'),
            title: {
              text: 'Branches'
            }
          },
          yaxis: {
            title: {
              text: 'Profit Amount (₹)'
            },
            labels: {
              formatter: function(value) {
                return '₹' + (value / 100000).toFixed(0) + 'L';
              }
            }
          },
          colors: ['#775DD0'],
          tooltip: {
            y: {
              formatter: function(value) {
                return '₹' + value.toLocaleString('en-IN');
              }
            }
          }
        }
      };
    },

    // Chart data for Branch Performance Distribution Pie Chart
    branchPerformanceChartData() {
      if (!this.records || this.records.length === 0) {
        return {
          series: [],
          options: {}
        };
      }

      const profitableBranches = this.records.filter(branch => (branch.totalProfit || 0) > 0).length;
      const lossBranches = this.records.filter(branch => (branch.totalProfit || 0) < 0).length;
      const breakEvenBranches = this.records.filter(branch => (branch.totalProfit || 0) === 0).length;

      return {
        series: [profitableBranches, lossBranches, breakEvenBranches],
        options: {
          chart: {
            type: 'pie',
            height: 350
          },
          labels: ['Profitable Branches', 'Loss Making Branches', 'Break Even Branches'],
          colors: ['#00E396', '#FF4560', '#FEB019'],
          legend: {
            position: 'bottom'
          },
          tooltip: {
            y: {
              formatter: function(value) {
                return value + ' branches';
              }
            }
          },
          responsive: [{
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }]
        }
      };
    },

    tableFields() {
      return [
        { key: 'sn', label: 'SN', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'branchId', label: 'Branch ID', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'branchLocation', label: 'Branch Location', thClass: 'text-center', tdClass: 'text-left' },
        { key: 'branchMainLocation', label: 'Branch Main Location', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalJobs', label: 'Total Jobs', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalInvoices', label: 'Total Invoices', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalExpenses', label: 'Total Expenses', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalProfit', label: 'Total Profit', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'avgInvoice', label: 'Avg Invoice', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'avgProfit', label: 'Avg Profit', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'profitPercentage', label: 'Profit %', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'status', label: 'Status', thClass: 'text-center', tdClass: 'text-center' }
      ];
    },

    onFiltered(filteredItems) {
      if (filteredItems && Array.isArray(filteredItems)) {
        this.totalRows = filteredItems.length;
        this.currentPage = 1;
      }
    }
  },
  watch: {
    filters: {
      handler() {
        this.loadData();
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    async loadData() {
      try {
        this.$emit('loading', true);

        const params = {};
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key] !== null && this.filters[key] !== '') {
            params[key] = this.filters[key];
          }
        });

        const response = await GrowthDashboardService.getBranchWiseGrowthData(params);

        if (response.data.success) {
          this.records = response.data.data.records;
          this.summary = response.data.data.summary;
          // Chart functionality temporarily disabled
          // this.$nextTick(() => {
          //   this.renderCharts();
          // });
        }
      } catch (error) {
        console.error('Error loading branch-wise growth data:', error);
        
      } finally {
        this.$emit('loading', false);
      }
    },

    // Chart functionality temporarily disabled
    renderCharts() {
      // Charts will be implemented when Chart.js is properly configured
      console.log('Chart rendering temporarily disabled');
    },

    renderTopBranchesChart() {
      // Chart functionality temporarily disabled
    },

    renderBranchPerformanceChart() {
      // Chart functionality temporarily disabled
    },

    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '₹0';
      return '₹' + Math.abs(amount).toLocaleString('en-IN');
    },

    getRowClass(record) {
      if (record.status === 'Loss') return 'danger';
      if (record.status === 'Profit') return 'success';
      return '';
    },

    getProfitClass(amount) {
      return amount >= 0 ? 'text-success' : 'text-danger';
    },

    getPercentageClass(percentage) {
      if (percentage > 0) return 'text-success';
      if (percentage < 0) return 'text-danger';
      return 'text-muted';
    },

    getStatusClass(status) {
      switch (status) {
        case 'Profit': return 'label-success';
        case 'Loss': return 'label-danger';
        default: return 'label-default';
      }
    },

    async exportToExcel() {
      try {
        if (!this.records || this.records.length === 0) {
          this.$notify({
            type: 'warning',
            title: 'Export',
            text: 'No branch-wise data available to export.'
          });
          return;
        }

        const excelRows = this.records.map((record, index) => ({
          SN: index + 1,
          'Branch ID': record.branchId || '',
          'Branch Location': record.branchLocation || '',
          'Branch Main Location': record.branchMainLocation || '',
          'Total Jobs': record.totalJobs || 0,
          'Total Invoices': record.totalInvoices || 0,
          'Total Expenses': record.totalExpenses || 0,
          'Total Profit': record.totalProfit || 0,
          'Avg Invoice': record.avgInvoice || 0,
          'Avg Profit': record.avgProfit || 0,
          'Profit %': record.profitPercentage || 0,
          Status: record.status || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Branch Growth');

        const timestamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Branch_Wise_Growth_${timestamp}.xlsx`);
      } catch (error) {
        console.error('Error exporting branch-wise data:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to export branch-wise data'
        });
      }
    }
  },

  mounted() {
    this.loadData();
  },

  beforeDestroy() {
    // Chart functionality temporarily disabled
    // No chart cleanup needed
  }
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.bg-gray {
  background-color: #f5f5f5 !important;
}

.label {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 4px;
}

.label-success {
  background-color: #5cb85c;
}

.label-danger {
  background-color: #d9534f;
}

.label-default {
  background-color: #999;
}

.small-box {
  margin-bottom: 15px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.text-success {
  color: #3c763d !important;
}

.text-danger {
  color: #a94442 !important;
}

.text-muted {
  color: #999 !important;
}
</style>



// WEBPACK FOOTER //
// BranchWiseGrowthComponent.vue?e681efe0